import { ApiItemType, CalendarEvent, CalendarEventListItem, RecurringEventCreateMode } from "../../model";
import { DatelyObject, Dates, ODataResult } from "../../util";
import { Ajax } from "../infra/Ajax";
import { CrudApiBase } from "../infra/CrudApiBase";

/**
 * Provides CRUD access for Calendar events and other calendar methods.
 * Note that in this API class, ID may contain also the type of the item.
 * I.e. id="09ddcb40-1c06-48cd-be07-ab16a6bdfccf/payrollDetails".
 * If the type is not defined, the default type is ApiItemType.CalendarEvent.
 */
export class CalendarEvents extends CrudApiBase<CalendarEvent, CalendarEventListItem> {

  /**
   * For NG1-dependency injection
   * @ignore
   */
  public static $inject = ["AjaxNg1"];

  /** Base URL for details etc. */
  protected baseUrl = "/v03-rc/api/calendar/events";

  /** URL for top-level (non calendar event) methods. */
  private calendarBaseUrl = "/v03-rc/api/calendar";

  constructor(ajax: Ajax) {
    super(ajax);
  }

  /** Client-side (synchronous) method for getting a new blank item as bases for UI binding. */
  public getBlank(): CalendarEvent {
    return {
      event: {
        isAllDay: true, // By default, we have all-day events of one day.
        start: Dates.getToday(),
        end: Dates.getToday(),
        duration: "1",
        actions: [],
        attachments: [],
        attendees: [],
        categories: [],
        recurrenceRules: [],
      },
    };
  }

  /**
   * Recalculates the occurence info based on the event.
   * This is an anonymous method that does not commit anything to database.
   * @param event Calendar event to recalculate.
   * @param start Start of the period for which the events are fetched and occurences are calculated.
   * If null, period is set 1 year before today. Max history/future 3 years. Max period 24 months.
   * @param end End of the period for which the occurences are calculated.
   * @returns A Promise with result data: The calendar event with occurenceInfo recalculated.
   */
  public recalculate(event: CalendarEvent, start: DatelyObject = null, end: DatelyObject = null): Promise<CalendarEvent> {
    return this.ajax.postJSON(`${this.baseUrl}/recalculate?start=${Dates.asDate(start) || ""}&end=${Dates.asDate(end) || ""}`, event);
  }

  /**
   * Gets the occurences for calendar events stored in the Salaxy calendar database.
   * @param categories Categories of occurences to fetch.
   * @param start Start of the period for which the events are fetched and occurences are calculated.
   * If null, period is set 1 year before today. Max history/future 3 years. Max period 24 months.
   * @param end End of the period for which the occurences are calculated.
   * @returns The occurences based on the given filters.
   */
  public getOccurences(categories: string[] = null, start: DatelyObject = null, end: DatelyObject = null): Promise<ODataResult<CalendarEventListItem>> {
    const catString = categories?.length ? categories.filter((x) => x?.trim()).map((x) => encodeURIComponent(x.trim())) : "";
    const url = `${this.calendarBaseUrl}/occurences?categories=${catString}&start=${Dates.asDate(start) || ""}&end=${Dates.asDate(end) || ""}`;
    return this.ajax.getJSON(url);
  }

  /**
   * Gets a Recurring event based on a business object that may be CalendarEvent of some other type that has potential recurrence.
   * @param id Unique identifier for the object.
   * The id may contain the payload type of the item, e.g. "09ddcb40-1c06-48cd-be07-ab16a6bdfccf/payrollDetails".
   * @param type Payload type as API object. This is only used, if the ID is simple ID without the
   * type (e.g. "09ddcb40-1c06-48cd-be07-ab16a6bdfccf").
   * If neither id nor type defines the Payload type, ApiItemType.CalendarEvent is the default.
   * @param mode Defines what kind of object is created if the existing object supports recurrence, but currently is not recurring.
   * Possibility to add the default recurrence rules, actions etc.
   * @returns The recurring event, potentially creating the default recurrence.
   */
  public getRecurringEvent(id: string, type: ApiItemType = null, mode = RecurringEventCreateMode.Default): Promise<CalendarEvent> {
    if (type && id.indexOf("/") < 0) {
      id = id + "/" + type;
    }
    const url = `${this.calendarBaseUrl}/recurring/${id}?mode=${mode}`;
    return this.ajax.getJSON(url);
  }
}
